import { useEffect, useRef } from "react";
import { MASK_ROWS, MASK_W, MASK_H } from "@/data/worldmask";
import { HUBS, ARCS } from "@/data/destinations";

const D2R = Math.PI / 180;
const TILT = -0.42;

const toVec = (lat, lon) => {
  const la = lat * D2R, lo = lon * D2R;
  return [Math.cos(la) * Math.sin(lo), Math.sin(la), Math.cos(la) * Math.cos(lo)];
};

const POINTS = [];
for (let y = 0; y < MASK_H; y += 2) {
  for (let x = 0; x < MASK_W; x += 2) {
    if (MASK_ROWS[y][x] === "1") {
      const lat = 90 - ((y + 0.5) / MASK_H) * 180;
      if (lat < -62) continue;
      const lon = ((x + 0.5) / MASK_W) * 360 - 180;
      POINTS.push(toVec(lat, lon));
    }
  }
}

const slerp = (a, b, t) => {
  const dot = Math.min(1, Math.max(-1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
  const th = Math.acos(dot);
  if (th < 1e-4) return a;
  const s = Math.sin(th);
  const ka = Math.sin((1 - t) * th) / s, kb = Math.sin(t * th) / s;
  return [a[0] * ka + b[0] * kb, a[1] * ka + b[1] * kb, a[2] * ka + b[2] * kb];
};

const ARC_PTS = ARCS.map(([ai, bi]) => {
  const a = toVec(HUBS[ai].lat, HUBS[ai].lon);
  const b = toVec(HUBS[bi].lat, HUBS[bi].lon);
  const pts = [];
  for (let i = 0; i <= 64; i++) {
    const t = i / 64;
    const v = slerp(a, b, t);
    const lift = 1 + 0.24 * Math.sin(Math.PI * t);
    pts.push([v[0] * lift, v[1] * lift, v[2] * lift]);
  }
  return pts;
});

const HUB_VECS = HUBS.map((h) => toVec(h.lat, h.lon));

const rotY = (v, r) => {
  const c = Math.cos(r), s = Math.sin(r);
  return [v[0] * c + v[2] * s, v[1], -v[0] * s + v[2] * c];
};
const tiltX = (v) => {
  const c = Math.cos(TILT), s = Math.sin(TILT);
  return [v[0], v[1] * c - v[2] * s, v[1] * s + v[2] * c];
};

export default function Globe({ className = "" }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let w = 0, h = 0, dpr = 1, raf;

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = rect.width; h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    const stars = Array.from({ length: 170 }, (_, i) => ({
      x: Math.random(),
      y: Math.random(),
      r: 0.4 + Math.random() * 1.3,
      phase: Math.random() * Math.PI * 2,
      speed: 0.3 + Math.random() * 0.9,
      bright: i < 10,
    }));
    const meteors = [];
    let lastMeteor = 0;

    const render = (time) => {
      const rot = reduced ? 0.9 : time * 0.00012;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      const R = Math.min(w, h) * 0.44;
      const cx = w / 2, cy = h / 2;

      const lx = cx + R * 0.55, ly = cy - R * 0.6;
      const light = ctx.createRadialGradient(lx, ly, 0, lx, ly, R * 1.5);
      light.addColorStop(0, "rgba(255,255,255,0.16)");
      light.addColorStop(0.35, "rgba(232,207,122,0.07)");
      light.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = light;
      ctx.fillRect(0, 0, w, h);

      const t = time * 0.001;
      for (const s of stars) {
        const sx = (s.x * w + t * s.speed * 14) % w;
        const sy = ((s.y * h + Math.sin(t * 0.4 + s.phase) * 6) % h + h) % h;
        const tw = 0.5 + 0.5 * Math.sin(t * s.speed * 3 + s.phase);
        const alpha = 0.15 + tw * 0.65;
        if (s.bright) {
          ctx.shadowColor = "rgba(255,255,255,0.9)";
          ctx.shadowBlur = 10;
        }
        ctx.fillStyle = `rgba(255,255,255,${alpha})`;
        ctx.beginPath();
        ctx.arc(sx, sy, s.r * (s.bright ? 1.4 : 1), 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      if (!reduced && time - lastMeteor > 4200 + Math.random() * 3500) {
        lastMeteor = time;
        meteors.push({
          x: Math.random() * w * 0.7 + w * 0.2,
          y: Math.random() * h * 0.35,
          p: 0,
          ang: Math.PI * (0.72 + Math.random() * 0.12),
        });
      }
      for (let i = meteors.length - 1; i >= 0; i--) {
        const m = meteors[i];
        m.p += 0.022;
        if (m.p >= 1) { meteors.splice(i, 1); continue; }
        const head = m.p * Math.min(w, h) * 0.55;
        const tail = head - Math.min(w, h) * 0.16;
        const hx = m.x + Math.cos(m.ang) * head, hy = m.y + Math.sin(m.ang) * head;
        const mx = m.x + Math.cos(m.ang) * tail, my = m.y + Math.sin(m.ang) * tail;
        const fade = Math.sin(m.p * Math.PI);
        const grad = ctx.createLinearGradient(hx, hy, mx, my);
        grad.addColorStop(0, `rgba(255,255,255,${0.9 * fade})`);
        grad.addColorStop(1, "rgba(255,255,255,0)");
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(hx, hy);
        ctx.lineTo(mx, my);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(hx, hy, 1.8, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${0.9 * fade})`;
        ctx.shadowColor = "rgba(255,255,255,0.9)";
        ctx.shadowBlur = 12;
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      const grd = ctx.createRadialGradient(cx, cy, R * 0.55, cx, cy, R * 1.25);
      grd.addColorStop(0, "rgba(212,175,55,0.07)");
      grd.addColorStop(1, "rgba(212,175,55,0)");
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, w, h);

      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(212,175,55,0.14)";
      ctx.lineWidth = 1;
      ctx.stroke();

      for (const p of POINTS) {
        const v = tiltX(rotY(p, rot));
        if (v[2] < -0.15) continue;
        const depth = (v[2] + 0.15) / 1.15;
        const alpha = 0.07 + depth * 0.55;
        ctx.fillStyle = depth > 0.82 ? `rgba(212,175,55,${alpha})` : `rgba(203,213,245,${alpha * 0.85})`;
        const r = 0.7 + depth * 1.1;
        ctx.beginPath();
        ctx.arc(cx + v[0] * R, cy - v[1] * R, r, 0, Math.PI * 2);
        ctx.fill();
      }

      ARC_PTS.forEach((pts, ai) => {
        ctx.beginPath();
        let started = false;
        let lastVisible = null;
        pts.forEach((p) => {
          const v = tiltX(rotY(p, rot));
          if (v[2] > 0.03) {
            const x = cx + v[0] * R, y = cy - v[1] * R;
            if (started) ctx.lineTo(x, y);
            else { ctx.moveTo(x, y); started = true; }
            lastVisible = [x, y];
          } else started = false;
        });
        ctx.strokeStyle = "rgba(212,175,55,0.5)";
        ctx.lineWidth = 1;
        ctx.stroke();

        if (lastVisible) {
          const idx = Math.floor(((time * 0.00008 + ai * 0.17) % 1) * 64);
          const v = tiltX(rotY(pts[idx], rot));
          if (v[2] > 0.03) {
            ctx.beginPath();
            ctx.arc(cx + v[0] * R, cy - v[1] * R, 2.4, 0, Math.PI * 2);
            ctx.fillStyle = "#e8cf7a";
            ctx.shadowColor = "rgba(212,175,55,0.9)";
            ctx.shadowBlur = 8;
            ctx.fill();
            ctx.shadowBlur = 0;
          }
        }
      });

      HUB_VECS.forEach((p) => {
        const v = tiltX(rotY(p, rot));
        if (v[2] > 0.1) {
          ctx.beginPath();
          ctx.arc(cx + v[0] * R, cy - v[1] * R, 1.8, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(232,207,122,0.95)";
          ctx.fill();
        }
      });

      if (!reduced) raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);

  return (
    <div ref={wrapRef} className={className} data-testid="hero-globe" aria-hidden="true">
      <canvas ref={canvasRef} className="block" />
    </div>
  );
}
